import React, {useState} from 'react';
import Select from 'react-select';
import b_ from 'b_';
import I18N from 'assets/i18n/brandlift-project';

import {
    useOrgUpdateRole,
    useOrgRole,
    useOrgAddRoleForUser,
    useOrgRemoveRoleForUser
} from 'client/common/hooks/orgs/roles';
import {Button, Icon, Textinput} from 'client/common/components/lego';
import Block from 'client/common/components/block';
import {UserImage} from 'client/common/components/user/user-image';
import {useOrgUsers} from 'client/common/hooks/orgs/users';

import './org-access.css';

const b = b_.with('org-access');

const i18n = I18N.orgAccess;

const permissionOptions = [
    {
        label: i18n.permissionGroups.read,
        options: [
            {value: 'read', content: i18n.permissionsMap.read},
            {value: 'readStats', content: i18n.permissionsMap.readStats},
            {value: 'readOrg', content: i18n.permissionsMap.readOrg}
        ]
    },
    {
        label: i18n.permissionGroups.edit,
        options: [
            {value: 'createSurvey', content: i18n.permissionsMap.createSurvey},
            {value: 'edit', content: i18n.permissionsMap.edit},
            {value: 'editCreatedBy', content: i18n.permissionsMap.editCreatedBy}
        ]
    },
    {
        label: i18n.permissionGroups.users,
        options: [
            {value: 'addUsers', content: i18n.permissionsMap.addUsers},
            {value: 'removeUsers', content: i18n.permissionsMap.removeUsers},
            {value: 'manageUserAccess', content: i18n.permissionsMap.manageUserAccess}
        ]
    },
    {
        label: i18n.permissionGroups.other,
        options: [
            {value: 'pay', content: i18n.permissionsMap.pay},
            {value: 'manage', content: i18n.permissionsMap.manage}
        ]
    }
];

const flatPermissionOptions = Object.entries(i18n.permissionsMap).map(([key, val]) => {
    return {value: key, content: val};
});

export type RolePermission = keyof typeof i18n.permissionsMap;

// eslint-disable-next-line max-statements
export function RoleEdit({
    orgId,
    roleId,
    name,
    permissions
}: {
    orgId: number;
    roleId: number;
    name: string;
    permissions: RolePermission[];
    // disabled: boolean;
}) {
    const {isSuccess, data: {users} = {users: []}} = useOrgRole(orgId, roleId);
    const {isSuccess: isSuccessUsers, data: orgUsersData} = useOrgUsers(orgId!);
    const addRoleForUser = useOrgAddRoleForUser(orgId, roleId);
    const removeRoleForUser = useOrgRemoveRoleForUser(orgId, roleId);

    const orgUsersOptions = orgUsersData?.map(user => {
        return {value: String(user.userid), content: `${user.login} — ${user.email}`};
    });

    const [isEditing, setIsEditing] = useState(false);
    const [roleName, setRoleName] = useState(name);
    const [rolePermissions, setRolePermissions] = useState(permissions);

    const updateOrgRole = useOrgUpdateRole(orgId, roleId);

    const handleToggleEditing = async () => {
        setIsEditing(!isEditing);

        if (isEditing) {
            await updateOrgRole({name: roleName, permissions: rolePermissions});
        }
    };

    const permissionValue = flatPermissionOptions.filter(option =>
        rolePermissions.includes(option.value as RolePermission)
    );

    return (
        <div className={b('role-edit')}>
            {isSuccess ? (
                <Block header={i18n.roleEdit.roleHeader}>
                    <div className={b('role-view')}>
                        <h5 className={b('role-view-label')}>{i18n.rolesTable.roleNameHeader}</h5>
                        <div>
                            {isEditing ? (
                                <Textinput
                                    className={b('role-view-label')}
                                    value={roleName}
                                    size='m'
                                    onChange={e => setRoleName(e.target.value)}
                                    state={roleName.trim() ? undefined : 'error'}
                                    view='dl'
                                />
                            ) : (
                                <span className={b('role-view-name')}>{roleName}</span>
                            )}
                        </div>

                        <h5 className={b('role-view-label')}>{i18n.rolesTable.roleAccessHeader}</h5>
                        <div className={b('role-view-permissions')}>
                            {isEditing ? (
                                <Select
                                    options={permissionOptions}
                                    value={permissionValue}
                                    size='m'
                                    view='default'
                                    getOptionLabel={option => option.content}
                                    isClearable={false}
                                    isSearchable
                                    isMulti
                                    placeholder={i18n.rolePermissionsPlaceholder.replace(
                                        '%s',
                                        roleName
                                    )}
                                    autoComplete='one-time-code'
                                    onChange={options => {
                                        const perms = options
                                            .map(option => option.value as RolePermission)
                                            .filter(Boolean);

                                        setRolePermissions(perms);
                                    }}
                                    styles={{
                                        multiValueLabel: base => ({...base, fontSize: 14}),
                                        menu: base => ({
                                            ...base,
                                            zIndex: 10,
                                            borderRadius: 12
                                        }),
                                        control: base => ({
                                            ...base,
                                            borderRadius: 12,
                                            minHeight: 44
                                        })
                                    }}
                                />
                            ) : (
                                rolePermissions.map(perm => {
                                    return (
                                        <span key={perm} className={b('permissions-label')}>
                                            {i18n.permissionsMap[perm]}
                                        </span>
                                    );
                                })
                            )}
                        </div>

                        {isSuccess && isSuccessUsers && (
                            <>
                                <h5 className={b('role-view-label')}>{i18n.roleEdit.users}</h5>
                                <div
                                    className={b('role-view-users', {
                                        mode: isEditing ? 'select' : 'avatars'
                                    })}
                                >
                                    {isEditing ? (
                                        <Select
                                            options={orgUsersOptions}
                                            value={users.map(usr => {
                                                return {
                                                    value: String(usr.userid),
                                                    content: usr.displayName
                                                };
                                            })}
                                            size='m'
                                            view='default'
                                            getOptionLabel={option => option.content}
                                            isClearable={false}
                                            isSearchable
                                            isMulti
                                            placeholder={i18n.roleEdit.addUserPlaceholder}
                                            autoComplete='one-time-code'
                                            onChange={async (_, e) => {
                                                if (e.action === 'select-option') {
                                                    return await addRoleForUser({
                                                        userid: parseInt(e.option?.value || '', 10)
                                                    });
                                                }

                                                if (
                                                    e.action === 'remove-value' ||
                                                    e.action === 'pop-value'
                                                ) {
                                                    return await removeRoleForUser({
                                                        userid: parseInt(e.removedValue.value, 10)
                                                    });
                                                }

                                                if (e.action === 'deselect-option') {
                                                    return await removeRoleForUser({
                                                        userid: parseInt(e.option?.value || '', 10)
                                                    });
                                                }
                                            }}
                                            styles={{
                                                multiValueLabel: base => ({...base, fontSize: 14}),
                                                menu: base => ({
                                                    ...base,
                                                    zIndex: 10,
                                                    borderRadius: 12
                                                }),
                                                control: base => ({
                                                    ...base,
                                                    borderRadius: 12,
                                                    minHeight: 44
                                                })
                                            }}
                                        />
                                    ) : (
                                        users.map(usr => {
                                            return (
                                                <div key={usr.email} className={b('user-image')}>
                                                    <UserImage
                                                        email={usr.email}
                                                        login={usr.login}
                                                        avatar={usr.avatar}
                                                        text={usr.displayName}
                                                    />
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </>
                        )}
                        <div />
                        <div>
                            <Button
                                view='dl-light'
                                size='m'
                                onClick={handleToggleEditing}
                                iconLeft={(cls: string) => (
                                    <Icon className={cls} type='edit-clear' size='s' />
                                )}
                            >
                                {isEditing ? i18n.saveRoleBtn : i18n.editRoleBtn}
                            </Button>
                        </div>
                    </div>
                </Block>
            ) : null}
        </div>
    );
}
